import { useState, useEffect, useRef } from 'react'
import { useSortable } from '@dnd-kit/react/sortable'
import { CollisionPriority } from '@dnd-kit/abstract'
import type { ReactNode } from 'react'
import css from './Section.module.css'
import { TextH2 } from '../../ui/Text'
import EditableText from '../../ui/EditableText/EditableText'
import IconButton from '@/components/ui/IconButton/IconButton'
import OptionsIcon from '@/assets/icons/icon-options.svg?react'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '../../ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import MoveUpIcon from '../../../assets/icons/icon-move-up.svg?react'
import MoveDownIcon from '../../../assets/icons/icon-move-down.svg?react'
import ArrowLeftIcon from '../../../assets/icons/icon-arrow-left.svg?react'
import ArrowRightIcon from '../../../assets/icons/icon-arrow-right.svg?react'
import IconCheck from '../../../assets/icons/icon-check.svg?react'
import IconCopy from '@/assets/icons/icon-copy.svg?react'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectOpenDropdownId } from '../../../store/ui/uiSelectors'
import { setOpenDropdown } from '../../../store/ui/uiSlice'
import { selectCanTransferList } from '../../../store/workspace/workspaceSelectors'
import { selectAllProjects } from '../../../store/projects/projectsSelectors'
import Link from '../Link/Link'
import SectionTaskComponent from './components/SectionTask/SectionTask'
import SectionAddTask from './components/SectionAddTask/SectionAddTask'
import type { SectionTask } from './types'
import { splitSectionTasks } from './sectionTasks'
import { prefetchDescriptionSaga } from '../../../store/tasks/tasksSlice'
import { usePublicShare } from '../../../contexts/PublicShareContext'
import DropdownDivider from "@/components/ui/Dropdown/components/DropdownDivider/DropdownDivider.tsx"

const SortableTasksContainer = ({ listId, listIndex, children }: { listId: string; listIndex: number; children: ReactNode }) => {
    const { ref } = useSortable({
        id: listId,
        accept: ['item'],
        collisionPriority: CollisionPriority.Low,
        type: 'list',
        index: listIndex,
    })
    return <div ref={ref as any} className={css.tasks}>{children}</div>
}

interface SectionProps {
    listId?: string
    listIndex?: number
    title: string
    // Rendered after the title as "· N". Passed only by the grouped project views, where the
    // size of each group is half the point of the mode; the section view stays countless.
    count?: number
    titleHref?: string
    onTitleUpdate?: (name: string) => Promise<void>
    onDeleteSection?: () => void
    onMoveUp?: () => void
    onMoveDown?: () => void
    tasks: SectionTask[]
    onTaskToggleComplete: (taskId: string, isCompleted: boolean) => void
    onTaskAssign?: (taskId: string, assigneeId: string | null) => void
    onAddTask?: (name: string) => void
    onAddTasks?: (names: string[]) => void
    showCompletedTasks?: boolean
    completedSortBy?: 'order' | 'recent'
    draggable?: boolean
    onTransfer?: (projectId: string) => void
    onAddSection?: () => void
    currentProjectId?: string
    // Passed only by the project list, where clicking a label chip filters the list. Without it
    // the chips render as plain text (My tasks, the public share view).
    onTaskLabelClick?: (labelId: string) => void
    activeLabelIds?: string[]
}

const Section = ({
    listId,
    listIndex = 0,
    title,
    count,
    titleHref,
    onTitleUpdate,
    onDeleteSection,
    onMoveUp,
    onMoveDown,
    tasks,
    onTaskToggleComplete,
    onTaskAssign,
    onAddTask,
    onAddTasks,
    showCompletedTasks,
    completedSortBy = 'order',
    draggable,
    onTransfer,
    onAddSection,
    currentProjectId,
    onTaskLabelClick,
    activeLabelIds
}: SectionProps) => {
    const dispatch = useAppDispatch()
    const openDropdownId = useAppSelector(selectOpenDropdownId)
    const allProjects = useAppSelector(selectAllProjects)
    const [isEditing, setIsEditing] = useState(false)
    const [editValue, setEditValue] = useState('')
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [dropdownPage, setDropdownPage] = useState<'main' | 'transfer'>('main')
    const [showAllCompleted, setShowAllCompleted] = useState(false)

    const dropdownIdRef = useRef(`section-${Math.random().toString(36).slice(2, 9)}`)
    const DROPDOWN_ID = dropdownIdRef.current

    const canTransfer = useAppSelector(selectCanTransferList)
    const publicShare = usePublicShare()

    const { open: openTasks, visibleCompleted, hiddenCompleted } = splitSectionTasks(tasks, {
        showCompletedTasks,
        completedSortBy,
        showAllCompleted,
    })
    const completedTotal = visibleCompleted.length + hiddenCompleted.length
    const hiddenCompletedCount = hiddenCompleted.length

    // Warm the descriptions of the tasks this section currently shows — open tasks plus the
    // completed ones above the "N completed tasks..." expander — so opening any of them is
    // instant. Idle-scheduled so it never competes with render; the prefetch worker is idempotent
    // and the ref keeps each id to a single dispatch. Lives in Section (not per page) so every
    // task list — project, My tasks, All tasks, Team — warms uniformly, using the same visible
    // set Section actually renders. Skipped in the public read-only view (no descriptions there).
    // Expanding the completed list grows `visibleCompleted`, so those rows are warmed too.
    const prefetchedRef = useRef<Set<string>>(new Set())
    const visibleTaskIds = [...openTasks, ...visibleCompleted].filter(t => !t.pending).map(t => t.id)
    const visibleTaskKey = visibleTaskIds.join(',')
    useEffect(() => {
        if (publicShare) return
        const pending = visibleTaskIds.filter(id => !prefetchedRef.current.has(id))
        if (pending.length === 0) return
        const run = () => {
            for (const id of pending) {
                prefetchedRef.current.add(id)
                dispatch(prefetchDescriptionSaga(id))
            }
        }
        if (typeof window.requestIdleCallback === 'function') {
            const handle = window.requestIdleCallback(run)
            return () => window.cancelIdleCallback?.(handle)
        }
        const handle = setTimeout(run, 200)
        return () => clearTimeout(handle)
        // visibleTaskKey captures the visible id set; re-runs only when it actually changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleTaskKey, publicShare, dispatch])

    const handleShowAllCompleted = () => {
        setShowAllCompleted(true)
    }

    useEffect(() => {
        setEditValue(title)
    }, [title])

    useEffect(() => {
        const shouldBeOpen = openDropdownId === DROPDOWN_ID
        if (isDropdownOpen !== shouldBeOpen) {
            setIsDropdownOpen(shouldBeOpen)
            if (!shouldBeOpen) {
                setDropdownPage('main')
            }
        }
    }, [openDropdownId])

    const handleStartEditing = () => {
        if (!onTitleUpdate) return
        setEditValue(title)
        setIsEditing(true)
    }

    const handleSave = async () => {
        setIsEditing(false)
        if (onTitleUpdate && editValue !== title) {
            await onTitleUpdate(editValue)
        }
    }

    const handleDropdownClick = () => {
        dispatch(setOpenDropdown(isDropdownOpen ? null : DROPDOWN_ID))
    }

    const handleDropdownClose = () => {
        dispatch(setOpenDropdown(null))
        setDropdownPage('main')
    }

    const handleMoveUp = () => {
        dispatch(setOpenDropdown(null))
        onMoveUp?.()
    }

    const handleMoveDown = () => {
        dispatch(setOpenDropdown(null))
        onMoveDown?.()
    }

    const handleDelete = () => {
        dispatch(setOpenDropdown(null))
        onDeleteSection?.()
    }

    const handleCopyId = () => {
        if (listId) navigator.clipboard.writeText(listId)
        dispatch(setOpenDropdown(null))
    }

    const handleTransfer = (projectId: string) => {
        dispatch(setOpenDropdown(null))
        setDropdownPage('main')
        onTransfer?.(projectId)
    }

    const handleAddSection = () => {
        dispatch(setOpenDropdown(null))
        onAddSection?.()
    }

    const sortedProjects = [...allProjects].sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    const currentValue = isEditing ? editValue : title
    const isEmpty = !currentValue || currentValue.trim() === ''
    const titleClassName = [
        css.title,
        isEmpty && css.titleEmpty,
        !onTitleUpdate && css.titleDisabled
    ].filter(Boolean).join(' ')

    return (
        <div className={css.item} data-list-id={listId}>
            <div className={css.header}>
                <div className={titleClassName}>
                    <TextH2>
                        {onTitleUpdate ? (
                            <EditableText
                                wrap={true}
                                isEditing={isEditing}
                                text={isEmpty && !isEditing ? 'Things to do' : editValue}
                                onChange={setEditValue}
                                onBlur={handleSave}
                                onStartEditing={handleStartEditing}
                            />
                        ) : titleHref ? (
                            <Link href={titleHref} className={css.titleLink}>{title || 'Things to do'}</Link>
                        ) : (
                            title || 'Things to do'
                        )}
                        {count !== undefined && <span className={css.count}>· {count}</span>}
                    </TextH2>
                </div>
                {(listId || onDeleteSection || onMoveUp || onMoveDown || onTransfer || onAddSection) && (
                    <div className={`${css.header_button}${isDropdownOpen ? ` ${css.header_button__visible}` : ''}`}>
                        <DropdownWrapper>
                            <IconButton grey={true} icon={<OptionsIcon />} onClick={handleDropdownClick} />
                            <Dropdown isOpen={isDropdownOpen} onClose={handleDropdownClose}>
                                {dropdownPage === 'main' ? (
                                    <>
                                        {listId && (
                                            <>
                                                <DropdownItem
                                                    icon={<IconCopy />}
                                                    title="Copy ID"
                                                    onClick={handleCopyId}
                                                />
                                                {(onMoveUp || onMoveDown || onTransfer || onAddSection || onDeleteSection) && (
                                                    <DropdownDivider />
                                                )}
                                            </>
                                        )}
                                        {onMoveUp && (
                                            <DropdownItem
                                                icon={<MoveUpIcon />}
                                                title="Move up"
                                                onClick={handleMoveUp}
                                            />
                                        )}
                                        {onMoveDown && (
                                            <DropdownItem
                                                icon={<MoveDownIcon />}
                                                title="Move down"
                                                onClick={handleMoveDown}
                                            />
                                        )}
                                        {(onMoveUp || onMoveDown) && (
                                            <DropdownDivider />
                                        )}
                                        {onTransfer && canTransfer && (
                                           <>
                                               <DropdownItem
                                                   title="Transfer to"
                                                   rightIcon={<ArrowRightIcon />}
                                                   onClick={() => setDropdownPage('transfer')}
                                               />
                                           </>
                                        )}
                                        {onAddSection && (
                                            <DropdownItem
                                                title="Add section"
                                                onClick={handleAddSection}
                                            />
                                        )}
                                        {(onDeleteSection && (onTransfer || onAddSection || onMoveUp || onMoveDown)) && (
                                            <DropdownDivider />
                                        )}
                                        {onDeleteSection && (
                                            <DropdownItem
                                                danger={true}
                                                title="Delete"
                                                onClick={handleDelete}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <DropdownItem
                                            icon={<ArrowLeftIcon />}
                                            title="Transfer to"
                                            muted={true}
                                            onClick={() => setDropdownPage('main')}
                                        />
                                        <DropdownDivider />
                                        {sortedProjects.map(project => {
                                            const isCurrent = project.id === currentProjectId
                                            return (
                                                <DropdownItem
                                                    key={project.id}
                                                    title={project.name || 'Untitled'}
                                                    rightIcon={isCurrent ? <IconCheck /> : undefined}
                                                    onClick={isCurrent ? undefined : () => handleTransfer(project.id)}
                                                />
                                            )
                                        })}
                                    </>
                                )}
                            </Dropdown>
                        </DropdownWrapper>
                    </div>
                )}
            </div>

            {draggable && listId ? (
                <SortableTasksContainer listId={listId} listIndex={listIndex}>
                    {openTasks.map((task, index) => (
                        <SectionTaskComponent
                            key={task.id}
                            task={task}
                            index={index}
                            listId={listId}
                            onToggleComplete={onTaskToggleComplete}
                            onAssign={onTaskAssign}
                            draggable={draggable}
                            onLabelClick={onTaskLabelClick}
                            activeLabelIds={activeLabelIds}
                        />
                    ))}

                    {onAddTask && <div className={css.add_task}>
                        <SectionAddTask onAdd={onAddTask} onAddMany={onAddTasks} /></div>}

                    {completedTotal > 0 && (
                        <div className={css.completed_tasks}>
                            {visibleCompleted.map((task, index) => (
                                <SectionTaskComponent
                                    key={task.id}
                                    task={task}
                                    index={index}
                                    onToggleComplete={onTaskToggleComplete}
                                    onLabelClick={onTaskLabelClick}
                                    activeLabelIds={activeLabelIds}
                                />
                            ))}
                            {hiddenCompletedCount > 0 && (
                                <button className={css.show_more} onClick={handleShowAllCompleted}>
                                    {completedTotal} completed tasks...
                                </button>
                            )}
                        </div>
                    )}
                </SortableTasksContainer>
            ) : (
                <div className={css.tasks}>
                    {openTasks.map((task, index) => (
                        <SectionTaskComponent
                            key={task.id}
                            task={task}
                            index={index}
                            onToggleComplete={onTaskToggleComplete}
                            onAssign={onTaskAssign}
                            draggable={draggable}
                            onLabelClick={onTaskLabelClick}
                            activeLabelIds={activeLabelIds}
                        />
                    ))}

                    {onAddTask && <div className={css.add_task}>
                        <SectionAddTask onAdd={onAddTask} onAddMany={onAddTasks} /></div>}

                    {completedTotal > 0 && (
                        <div className={css.completed_tasks}>
                            {visibleCompleted.map((task, index) => (
                                <SectionTaskComponent
                                    key={task.id}
                                    task={task}
                                    index={index}
                                    onToggleComplete={onTaskToggleComplete}
                                    onLabelClick={onTaskLabelClick}
                                    activeLabelIds={activeLabelIds}
                                />
                            ))}
                            {hiddenCompletedCount > 0 && (
                                <button className={css.show_more} onClick={handleShowAllCompleted}>
                                    {completedTotal} completed tasks...
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default Section
export type { SectionTask, SectionProps }
